import { motion } from "framer-motion";
import { Reveal } from "../Reveal";
import { Sparkles, Leaf, Dumbbell } from "lucide-react";

const pillars = [
  {
    key: "M",
    title: "Mindset",
    tint: "bg-mne-pink",
    icon: Sparkles,
    body:
      "We reset the inner narrative — building resilience, self-belief and calm so you can meet change with clarity instead of overwhelm.",
    points: ["Confidence coaching", "Stress & sleep", "Goal setting"],
  },
  {
    key: "N",
    title: "Nutrition",
    tint: "bg-mne-green",
    icon: Leaf,
    body:
      "Nourishment that fits real life. No fads — just balanced, hormone-friendly eating that steadies energy and supports your body through midlife.",
    points: ["Hormone support", "Simple meal plans", "Sustainable habits"],
  },
  {
    key: "E",
    title: "Exercise",
    tint: "bg-mne-blue",
    icon: Dumbbell,
    body:
      "Movement you'll actually enjoy. Strength, mobility and stamina tailored to your body today — protecting bones, joints and long-term vitality.",
    points: ["Strength training", "Mobility", "Personal programmes"],
  },
];

export default function MNEFormula() {
  return (
    <section id="formula" data-testid="formula-section" className="relative py-24 md:py-36 bg-linen border-y border-border">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex items-center gap-4 mb-14">
            <span className="font-serif text-2xl text-gold">02</span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-stone">The Formula</span>
            <span className="flex-1 h-px bg-border" />
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10 items-center mb-16">
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.02] text-plum">
                One simple formula for
                <span className="italic text-stone"> optimal health</span> after 40.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:justify-self-end w-full">
            <Reveal delay={0.15}>
              <img src="/assets/mne-logo.png" alt="MNE — Mindset Nutrition Exercise" className="w-full max-w-[560px] rounded-[6px] border border-border shadow-md" />
            </Reveal>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.key}
                data-testid={`pillar-${p.title.toLowerCase()}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 }}
                whileHover={{ y: -8 }}
                className="group relative overflow-hidden rounded-[6px] border border-border bg-cream p-8 md:p-10"
              >
                <div className={`absolute -top-16 -right-16 h-40 w-40 rounded-full ${p.tint} opacity-40 blur-2xl transition-opacity duration-500 group-hover:opacity-70`} />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-7xl text-plum/15 leading-none">{p.key}</span>
                    <span className={`flex h-14 w-14 items-center justify-center rounded-full ${p.tint}`}>
                      <Icon size={22} className="text-plum" strokeWidth={1.6} />
                    </span>
                  </div>
                  <h3 className="mt-6 font-serif text-3xl text-plum">{p.title}</h3>
                  <p className="mt-4 text-[15px] text-stone leading-relaxed">{p.body}</p>
                  <ul className="mt-6 space-y-2">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-3 text-[13px] uppercase tracking-[0.12em] text-plum/80">
                        <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
